/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import { formatISO } from 'date-fns';
import React from 'react';
import { Link } from 'wouter';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { PaymentInfo } from '../../api/endpoints/admin';
import { Currency } from '../../platform/Currency';
import { InfoView } from '../../ui/InfoView';
import { LoadingBar } from '../../ui/LoadingBar';
import { PaymentCard } from '../../ui/PaymentCard';
import { AppEvents } from '../AppEvents';
import { useResolve } from '../Container';
import { ButtonStrip, Copy, Em, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { inputSubmitPopup } from '../popups/InputPopup';
import { PopupManager } from '../popups/PopupManager';
import { UpgradeButton } from '../upgrade/UpgradeButton';
import { ManageBillingSettingsModel, ManageBillingSettingsViewScope } from './ManageBillingSettingsViewScope';

const { Section, LargeHeading, Heading, HeadingAction, DetailTable, DetailRow, DetailCell, DetailAction } = InfoView;

const LimitItem = React.memo<{ label: string, value: number, total: number }>(({ label, value, total }) => <>
    <LoadingBar progress={value / total} />
</>);

const FreeOrCustomSection = React.memo(() => {
    const { plan, subscription, trial } = useObserver(useResolve(ManageBillingSettingsModel));
    if (subscription || trial?.state === 'active') return null;
    return <Section>
        <Flexer maxWidth="42">
            <Heading>You are on the {plan.label} Plan</Heading>
            {trial && <Copy>Your free trial has now expired.  Your account will continue on the free plan unless you upgrade.  On the free plan, you are subject to a limit of {plan.features.objectLimit} objects across all boards.</Copy>}
            {!trial && plan.name.includes('free') && <Copy>On the free plan, you are subject to a limit of {plan.features.objectLimit} objects across all boards.</Copy>}
            {!plan.name.includes('free') && <Copy>Your billing configuration is bespoke, please contact support for changes.</Copy>}
            <Spacer f={1} />
            {plan.name.includes('free') && <ButtonStrip>
                <UpgradeButton />
                <UpgradeButton variant="common" label="Start Trial" />
            </ButtonStrip>}
        </Flexer>
    </Section>;
});

const TrialSection = React.memo(() => {
    const { subscription, trial, plan } = useObserver(useResolve(ManageBillingSettingsModel));
    if (subscription || trial?.state !== 'active') return null;
    return <Section>
        <Heading>You are on a free trial of the {plan.label.replace('Trial', '')} Plan.</Heading>
        <Copy>Your trial will finish on <Em>{trial.expiresAt?.toLocaleDateString()}</Em>.</Copy>
        <Spacer f={3} />
        <ButtonStrip>
            <UpgradeButton />
        </ButtonStrip>
    </Section>;
});

const FormatPaymentInfo = React.memo<{ info?: PaymentInfo, action: string }>(({ info, action }) =>
    info && <span>{Currency.symbolFor(info.currency)}{info.amount.toFixed(2)} {action} {info.date}</span>
);

const setLimitPopup = (submit: (value: number) => Promise<void>) => inputSubmitPopup({
    title: 'Set Quantity Limit',
    placeholder: 'None',
    copy: 'Please specify the seat limit for your organization:',
    parse: x => parseInt(x) || null,
    submit,
});

const SubscriptionSection = React.memo(() => {

    const api = useResolve(ApiClient);
    const events = useResolve(AppEvents);
    const popups = useResolve(PopupManager);

    const { plan, subscription, settings } = useObserver(useResolve(ManageBillingSettingsModel));
    if (!subscription) return null;

    const onSetLimit = async () => {
        await popups.invoke(setLimitPopup(async (value: number) => {
            const data = { 'billing.userLimit': value };
            await api.exec(v2.settings.patch(data));
            events.push('settings:patch', data);
        }));
    };

    return <Section>
        <Heading>You are on the {plan.label} Plan
            {subscription.state === 'active' &&
                <HeadingAction onClick={null}>
                    <a target="_blank" href="/checkout/change">Change Plan</a>
                </HeadingAction>
            }
        </Heading>
        <DetailTable>
            {subscription?.interval &&
                <DetailRow>
                    <DetailCell shrink>Billing Interval</DetailCell>
                    <DetailCell>
                        <span style={{ textTransform: 'capitalize' }}>{subscription?.interval}ly</span>
                    </DetailCell>
                </DetailRow>
            }
            {subscription?.nextPayment &&
                <DetailRow>
                    <DetailCell shrink>Next Payment</DetailCell>
                    <DetailCell>
                        <FormatPaymentInfo info={subscription.nextPayment} action="due on" />
                    </DetailCell>
                </DetailRow>
            }
            {subscription?.lastPayment &&
                <DetailRow>
                    <DetailCell shrink>Last Payment</DetailCell>
                    <DetailCell>
                        <FormatPaymentInfo info={subscription.lastPayment} action="processed on" />
                        <DetailAction>
                            <Link to="~/manage/billing/payments">
                                View Payments
                            </Link>
                        </DetailAction>
                    </DetailCell>
                </DetailRow>
            }
            {subscription?.quantity !== undefined && <>
                <DetailRow>
                    <DetailCell shrink>Seats</DetailCell>
                    <DetailCell>
                        {subscription.quantity}
                        {subscription.interval === 'year' &&
                            <DetailAction>
                                <a target="_blank" href="/checkout/add-seats">Add Seats</a>
                            </DetailAction>
                        }
                    </DetailCell>
                </DetailRow>
                {!!settings.compSeats && <DetailRow>
                    <DetailCell shrink>Comp Seats</DetailCell>
                    <DetailCell>
                        {settings.compSeats}
                    </DetailCell>
                </DetailRow>
                }
            </>}
            {subscription.state === 'active' && subscription.interval !== 'year' && <DetailRow>
                <DetailCell shrink>
                    Seat Limit:
                </DetailCell>
                <DetailCell>
                    {settings?.userLimit ?? 'None'}
                    <DetailAction onClick={onSetLimit}>
                        Set Limit
                    </DetailAction>
                </DetailCell>
            </DetailRow>}
            {subscription?.userEmail &&
                <DetailRow>
                    <DetailCell shrink>Billing Email</DetailCell>
                    <DetailCell>{subscription.userEmail}</DetailCell>
                </DetailRow>
            }
        </DetailTable>
    </Section>;
});

const PaymentMethodSection = React.memo(() => {

    const { plan, subscription } = useObserver(useResolve(ManageBillingSettingsModel));
    if (!subscription) return null;

    const { paymentInformation: info, updateUrl } = subscription;

    if (!info) return null;
    return <Section>
        <Heading>
            <span>Payment Method</span>
            <HeadingAction onClick={null}>
                <a target="_blank" rel="noreferrer" href={updateUrl}>Change Payment Method</a></HeadingAction>
        </Heading>
        <PaymentCard type={info.cardType} digits={info.lastFourDigits} expiry={info.expiryDate} />
    </Section>;
});

const CancellationSection = React.memo(() => {

    const { plan, subscription } = useObserver(useResolve(ManageBillingSettingsModel));
    if (subscription?.state === 'deleted') {

        const expiresOn = (!!subscription?.expiresAt ? formatISO(subscription?.expiresAt)?.split('T')[0] : null) ?? '???';

        return <Section>
            <Heading>
                Your subscription has been cancelled.  Your account will revert to the Free Plan on {expiresOn}.
            </Heading>
        </Section>;
    }
    else if (!!subscription) {
        return <Section>
            <Heading>
                Cancel Subscription
            </Heading>
            <Copy>
                To cancel your subscription, <a target="_blank" href={subscription.cancelUrl} rel="noreferrer">please click here</a>.
            </Copy>
        </Section>;
    }
    else {
        return null;
    }
});

export const ManageBillingSettingsView = () =>
    <ManageBillingSettingsViewScope>
        <Section>
            <LargeHeading>
                Plan &amp; Subscription
            </LargeHeading>
            <Copy>From here you can manage your plan, payment method and subscription settings.</Copy>
        </Section>
        <FreeOrCustomSection />
        <SubscriptionSection />
        <TrialSection />
        <Spacer />
        <PaymentMethodSection />
        <Spacer />
        <CancellationSection />
    </ManageBillingSettingsViewScope>
    ;
